/**
 * Read the typed error shape our API returns (see packages/trpc errorFormatter):
 * data.code (tRPC code), data.domainCode, data.fieldErrors, data.requestId.
 * Works on any thrown value, so UI code never has to guess.
 */
interface ErrorData {
  code?: string;
  httpStatus?: number;
  domainCode?: string | null;
  fieldErrors?: Record<string, string[] | undefined> | null;
  requestId?: string;
}

const dataOf = (err: unknown): ErrorData | undefined => {
  if (typeof err !== "object" || err === null || !("data" in err)) return undefined;
  const { data } = err;
  return typeof data === "object" && data !== null ? data : undefined;
};

export const getErrorCode = (err: unknown): string | undefined => dataOf(err)?.code;

export const getDomainCode = (err: unknown): string | undefined =>
  dataOf(err)?.domainCode ?? undefined;

export const getRequestId = (err: unknown): string | undefined => dataOf(err)?.requestId;

export const isUnauthorized = (err: unknown): boolean =>
  getErrorCode(err) === "UNAUTHORIZED" || dataOf(err)?.httpStatus === 401;

/** The request itself was refused (not found, forbidden, invalid): trying again can't help. */
export const isClientError = (err: unknown): boolean => {
  const status = dataOf(err)?.httpStatus;
  return status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;
};

/** First message per field, ready to show next to inputs. */
export const getFieldErrors = (err: unknown): Record<string, string> => {
  const fields = dataOf(err)?.fieldErrors ?? {};
  const result: Record<string, string> = {};
  for (const [field, messages] of Object.entries(fields)) {
    if (messages?.[0]) result[field] = messages[0];
  }
  return result;
};

/** A message safe to show a person. */
export const getErrorMessage = (err: unknown, fallback = "Something went wrong"): string =>
  err instanceof Error && err.message ? err.message : fallback;
